import { Heading } from '@/components/Heading'
import { Box } from '@/components/Box'
import Input from '@/components/Input'
import { Button } from '@/components/Button'
import { Question } from '@/components/Question'
import Textarea from '@/components/Textarea'
import { Tooltip } from 'react-tooltip'
import { ROUTES } from '@/config/routes'
import { TooltipIcon } from '@/components/TooltipIcon'
import { Header } from '@/components/HeaderBusiness'
import usePersistedNavigation from '@/hooks/usePersistedNavigation'
import { useContext, useState, useEffect } from 'react'
import { DealerContext } from '@/context/DealerContext'
import useFormContext from '@/context/useFormContext'
import { useForm } from 'react-hook-form'
import { ReactSVG } from 'react-svg'
import { useFormValidation } from '@/context/FormValidationContext'
import { useFormSubmission } from '@/hooks/useFormSubmission'
import { preparePayload } from '@/services/saveService'
import { validateMoney } from '@/utils/validation'

function FinancialPosition() {
  const navigate = usePersistedNavigation()
  const { state, dispatch } = useFormContext()
  const dealerContext = useContext(DealerContext)
  const [totalMonthlyPayments, setTotalMonthlyPayments] = useState(0)
  const { register, watch, formState: { errors, isSubmitted }, handleSubmit, setValue, trigger } = useForm({
    defaultValues: {
      isOwnHome: state.business.financialPosition.isOwnHome,
      mortgateValue: state.business.financialPosition.mortgateValue,
      mortgateBalance: state.business.financialPosition.mortgateBalance,
      mortgateMonthlyPayment: state.business.financialPosition.mortgateMonthlyPayment,
      vehicleValue: state.business.financialPosition.vehicleValue,
      vehicleAmount: state.business.financialPosition.vehicleAmount,
      vehicleMonthlyPayment: state.business.financialPosition.vehicleMonthlyPayment,
      furnitureValue: state.business.financialPosition.furnitureValue,
      furnitureAmount: state.business.financialPosition.furnitureAmount,
      furnitureMonthlyPayment: state.business.financialPosition.furnitureMonthlyPayment,
      personalLoanValue: state.business.financialPosition.personalLoanValue,
      personalLoanAmount: state.business.financialPosition.personalLoanAmount,
      personalLoanMonthlyPayment: state.business.financialPosition.personalLoanMonthlyPayment,
      creditCardValue: state.business.financialPosition.creditCardValue,
      creditCardAmount: state.business.financialPosition.creditCardAmount,
      creditCardMonthlyPayment: state.business.financialPosition.creditCardMonthlyPayment,
      otherValue: state.business.financialPosition.otherValue,
      otherAmount: state.business.financialPosition.otherAmount,
      otherMonthlyPayment: state.business.financialPosition.otherMonthlyPayment,
      additionalNotes: state.business.financialPosition.additionalNotes || '',
      isContinue: state.business.financialPosition.isContinue || false
    }
  })

  const [showFullForm, setShowFullForm] = useState(state.business.financialPosition.isContinue)

  useEffect(() => {
    register('isOwnHome', { 
      validate: (value) => value === true || value === false || 'This field is required'
    })
  })
  // Watch all payment fields to calculate total
  const mortgateMonthlyPayment = watch('mortgateMonthlyPayment')
  const vehicleMonthlyPayment = watch('vehicleMonthlyPayment')
  const furnitureMonthlyPayment = watch('furnitureMonthlyPayment')
  const personalLoanMonthlyPayment = watch('personalLoanMonthlyPayment')
  const creditCardMonthlyPayment = watch('creditCardMonthlyPayment')
  const otherMonthlyPayment = watch('otherMonthlyPayment')
  const ownsHome = watch('isOwnHome') === true

  const getSVGIcon = (icon: string, defaultIcon: string) => {
    return dealerContext?.dealerThemeIcon.businessFinancialPosition.find(res => res.option === icon)?.icon || defaultIcon
  }

  const handleSelectChange = (fieldName: string, value: string) => {
    // @ts-expect-error - We know it's one possible value of the form, but we don't want to create yet another type for this
    setValue(fieldName, value)
    // @ts-expect-error - We know it's one possible value of the form, but we don't want to create yet another type for this
    if (isSubmitted) trigger(fieldName)
  }

  const calculateTotalMonthlyPayments = () => {
    let total = 0
    // Add all other monthly payments
    total += parseFloat(String(vehicleMonthlyPayment || '0'))
    total += parseFloat(String(furnitureMonthlyPayment || '0'))
    total += parseFloat(String(personalLoanMonthlyPayment || '0'))
    total += parseFloat(String(creditCardMonthlyPayment || '0'))
    total += parseFloat(String(otherMonthlyPayment || '0'))

    setTotalMonthlyPayments(total)
  }

  // Calculate total whenever any payment field changes
  useEffect(() => {
    calculateTotalMonthlyPayments()
  }, [
    mortgateMonthlyPayment,
    vehicleMonthlyPayment,
    furnitureMonthlyPayment,
    personalLoanMonthlyPayment,
    creditCardMonthlyPayment,
    otherMonthlyPayment,
    ownsHome
  ])

  const { registerValidationTrigger, unregisterValidationTrigger } = useFormValidation()
  const triggerFormValidation = () => handleSubmit(() => {})()

  useEffect(() => {
    registerValidationTrigger(triggerFormValidation)
    return () => unregisterValidationTrigger()
  }, [registerValidationTrigger, unregisterValidationTrigger])

  const { handleFormSave } = useFormSubmission({
    onSuccess: (data) => {
      console.log('Form submission successful:', data)
    },
    onError: (error) => {
      console.error('Error during form save:', error)
    }
  })

  const dispatchFormData = (data: any) => {
    console.log('Form dispatch:', data)
    dispatch({
      type: 'UPDATE_BUSINESS',
      step: 'financialPosition',
      payload: {
        ...data.business.financialPosition,
        isContinue: true
      }
    })
  }

  const handleContinue = async () => {
    const isValid = await trigger()
    if (isValid) {
      setShowFullForm(true)
      dispatch({
        type: 'UPDATE_BUSINESS',
        step: 'financialPosition',
        payload: {
          ...state.business.financialPosition,
          isContinue: true
        }
      })
    }
  }

  const handleNext = async (data: any) => {
    const isValid = await trigger()
    if (isValid) {
      onSubmit(data)
    }
  }

  const onSubmit = (data: any) => {
    // Clone aboutYourBusiness and apply the null logic
    const aboutYourBusiness = { ...state.business.aboutYourBusiness }
    if (aboutYourBusiness.businessType === 'SOLE') {
      aboutYourBusiness.limitedCompany = null
    } else if (aboutYourBusiness.businessType === 'LIMITED') {
      aboutYourBusiness.soleTrader = null
    }

    // Convert string values to numbers for the business asset
    data = {
      ...state,
      personal: null,
      business: {
        ...state.business,
        aboutYourBusiness, // updated with null logic
        financialPosition: {
          isOwnHome: data.isOwnHome,
          mortgateValue: data.mortgateValue ? parseFloat(data.mortgateValue) : null,
          mortgateBalance: data.mortgateBalance ? parseFloat(data.mortgateBalance) : null,
          mortgateMonthlyPayment: data.mortgateMonthlyPayment ? parseFloat(data.mortgateMonthlyPayment) : null,
          vehicleValue: data.vehicleValue ? parseFloat(data.vehicleValue) : null,
          vehicleAmount: data.vehicleAmount ? parseFloat(data.vehicleAmount) : null,
          vehicleMonthlyPayment: data.vehicleMonthlyPayment ? parseFloat(data.vehicleMonthlyPayment) : null,
          furnitureValue: data.furnitureValue ? parseFloat(data.furnitureValue) : null,
          furnitureAmount: data.furnitureAmount ? parseFloat(data.furnitureAmount) : null,
          furnitureMonthlyPayment: data.furnitureMonthlyPayment ? parseFloat(data.furnitureMonthlyPayment) : null,
          personalLoanValue: data.personalLoanValue ? parseFloat(data.personalLoanValue) : null,
          personalLoanAmount: data.personalLoanAmount ? parseFloat(data.personalLoanAmount) : null,
          personalLoanMonthlyPayment: data.personalLoanMonthlyPayment ? parseFloat(data.personalLoanMonthlyPayment) : null,
          creditCardValue: data.creditCardValue ? parseFloat(data.creditCardValue) : null,
          creditCardAmount: data.creditCardAmount ? parseFloat(data.creditCardAmount) : null,
          creditCardMonthlyPayment: data.creditCardMonthlyPayment ? parseFloat(data.creditCardMonthlyPayment) : null,
          otherValue: data.otherValue ? parseFloat(data.otherValue) : null,
          otherAmount: data.otherAmount ? parseFloat(data.otherAmount) : null,
          otherMonthlyPayment: data.otherMonthlyPayment ? parseFloat(data.otherMonthlyPayment) : null,
          additionalNotes: data.additionalNotes
        }
      }
    }
    
    dispatchFormData(data)
    handleFormSave(preparePayload(data, 'business', 'financialPosition'), ROUTES.BUSINESS.APPLICATION_OPTIONS.path)
  }

  const [isDealer, setIsDealer] = useState(false)
  useEffect(() => {
    if (dealerContext?.dealerInformation?.dealerUniqueIdentifier) {
      setIsDealer(true)
    }
  }, [dealerContext])

  return (
    <>
      <Header />
      <Tooltip className='Tooltip Tooltip--info' id='tooltip-holder' />
      <Heading>Assets and liabilities</Heading>
      <form onSubmit={handleSubmit(handleNext)}>
        <Box>
          <Question
            title='Do you rent or own your home? *'
            name='isOwnHome'
            defaultValue={state.business.financialPosition.isOwnHome}
            onChange={value => handleSelectChange('isOwnHome', value)}
            error={errors.isOwnHome}
            options={[
              { id: 'ownHomeYes', title: 'Own', value: true },
              { id: 'ownHomeNo', title: 'Rent', value: false }
            ]}
          />
          <div>
            <div className='grid grid-cols-1 lg:grid-cols-4 border-b border-[var(--form-box-divider)] justify-end mb-0 max-lg:hidden'>
              <p className='col-start-2 pb-3 text-sm font-[var(--font-weight-bold)] pl-6'>Value</p>
              <p className='col-start-3 pb-3 text-sm font-[var(--font-weight-bold)] pl-6'>Mortgage balance</p>
              <p className='col-start-4 pb-3 text-sm font-[var(--font-weight-bold)] pl-6'>Monthly payments</p>
            </div>
            <div className='grid grid-cols-1 lg:grid-cols-4 gap-5 lg:gap-8 _hide-label-lg border-b border-[var(--form-box-divider)] pt-2 px-3 lg:px-0 pb-8 lg:pb-4 mb-2 last:mb-0 lg:mb-0'>
              <div className='pt-2'>
                <div className='flex w-full items-center gap-5'>
                  <span className='size-8 flex items-center justify-center text-[var(--theme-icon)]'>
                    {!isDealer ?
                      <ReactSVG className='size-full flex items-center justify-center' src='/icons/financial-position/udc/house.svg' aria-label='Home'/>
                      :
                      <ReactSVG className='FinancialPosition-svg size-full flex items-center justify-center' src={getSVGIcon('home', '/icons/financial-position/house.svg')} aria-label='Home'/>
                    }
                  </span>
                  <p className='text-lg/7 mt-0.5'>Home</p>
                </div>
              </div>
              {ownsHome === true &&
                <>
                  <Input
                    label='Value'
                    placeholder='0 *'
                    inputId='mortgateValue'
                    {...register('mortgateValue', { required: true, validate: (value) => validateMoney(true, value) })}
                    error={errors.mortgateValue}
                    money={true}
                  />
                  <Input
                    label='Mortgage balance'
                    placeholder='0 *'
                    inputId='mortgateBalance'
                    {...register('mortgateBalance', { required: true, validate: (value) => validateMoney(true, value) })}
                    error={errors.mortgateBalance}
                    money={true}
                  />
                  <Input
                    label='Monthly payments'
                    placeholder='0 *'
                    inputId='mortgateMonthlyPayment'
                    {...register('mortgateMonthlyPayment', { required: true, validate: (value) => validateMoney(true, value) })}
                    error={errors.mortgateMonthlyPayment}
                    money={true}
                  />
                </>
              }
              {ownsHome === false &&
                <div className='col-start-1 lg:col-start-4'>
                  <Input
                    label='Monthly payments'
                    placeholder='0 *'
                    inputId='mortgateMonthlyPayment'
                    {...register('mortgateMonthlyPayment', { required: true, validate: (value) => validateMoney(true, value) })}
                    error={errors.mortgateMonthlyPayment}
                    money={true}
                  />
                </div>
              }
            </div>
          </div>
        </Box>
        
        {!showFullForm && 
          <div className='flex justify-between mt-8 pb-16 max-w-5xl mx-auto'>
            <Button iconPosition='left' onClick={() => navigate(ROUTES.BUSINESS.ABOUT_YOU.path)}>Back</Button>
            <Button type='button' onClick={handleContinue}>Continue</Button>
          </div>
        }
        {showFullForm &&
          <>
            {/* Please leave on one line */}
            <Heading><span>What are your combined  Financial <span className='whitespace-nowrap'>commitments <TooltipIcon id='tooltip-holder' content='Tell us about any ongoing financial commitments you have. E.g. mortgage or credit card payments, student loans or any personal loans' /></span></span></Heading>
            <Box>
              <div>
                <div className='grid grid-cols-1 lg:grid-cols-4 border-b lg:gap-8 border-[var(--form-box-divider)] justify-end pt-12 mb-0 max-lg:hidden'>
                  <p className='col-start-2 pb-3 text-sm font-[var(--font-weight-bold)]'>Value</p>
                  <p className='col-start-3 pb-3 text-sm font-[var(--font-weight-bold)]'>Amount owed</p>
                  <p className='col-start-4 pb-3 text-sm font-[var(--font-weight-bold)]'>Monthly payments</p>
                </div>
                <div className='grid grid-cols-1 lg:grid-cols-4 gap-5 lg:gap-8 _hide-label-lg border-b border-[var(--form-box-divider)] pt-2 px-3 lg:px-0 pb-8 lg:pb-4 mb-2 last:mb-0 lg:mb-0'>
                  <div className='pt-2'>
                    <div className='flex w-full items-center gap-5'>
                      <span className='size-8 shrink-0 flex items-center justify-center text-[var(--theme-icon)]'>
                        {!isDealer ?
                          <ReactSVG className='size-full flex items-center justify-center' src='/icons/financial-position/udc/vehicles.svg' aria-label='Vehicles'/>
                          :
                          <ReactSVG className='FinancialPosition-svg size-full flex items-center justify-center' src={getSVGIcon('vehicles', '/icons/financial-position/vehicles.svg')} aria-label='Vehicles'/>
                        }
                      </span>
                      <p className='text-lg/7 mt-0.5 text-pretty'>Vehicles</p>
                    </div>
                  </div>
                  <Input
                    label='Value'
                    placeholder='0'
                    inputId='vehicleValue'
                    defaultValue={state.business?.financialPosition?.vehicleValue || ''}
                    {...register('vehicleValue', { validate: (value) => validateMoney(false, value) })}
                    error={errors.vehicleValue}
                    money={true}
                  />
                  <Input
                    label='Amount owed'
                    placeholder='0'
                    inputId='vehicleAmount'
                    defaultValue={state.business?.financialPosition?.vehicleAmount || ''}
                    {...register('vehicleAmount', { validate: (value) => validateMoney(false, value) })}
                    error={errors.vehicleAmount}
                    money={true}
                  />
                  <Input
                    label='Monthly payments'
                    placeholder='0'
                    inputId='vehicleMonthlyPayment'
                    defaultValue={state.business?.financialPosition?.vehicleMonthlyPayment || ''}
                    {...register('vehicleMonthlyPayment', { validate: (value) => validateMoney(false, value) })}
                    error={errors.vehicleMonthlyPayment}
                    money={true}
                  />
                </div>
                <div className='grid grid-cols-1 lg:grid-cols-4 gap-5 lg:gap-8 _hide-label-lg border-b border-[var(--form-box-divider)] pt-2 px-3 lg:px-0 pb-8 lg:pb-4 mb-2 last:mb-0 lg:mb-0'>
                  <div className='pt-2'>
                    <div className='flex w-full items-center gap-5'>
                      <span className='size-8 shrink-0 flex items-center justify-center text-[var(--theme-icon)]'>
                        {!isDealer ?
                          <ReactSVG className='size-full flex items-center justify-center' src='/icons/financial-position/udc/furniture-and-household-items.svg' aria-label='Furniture and household items'/>
                          :
                          <ReactSVG className='FinancialPosition-svg size-full flex items-center justify-center' src={getSVGIcon('furnitureAndHouseholdItems', '/icons/financial-position/furniture-and-household-items.svg')} aria-label='Furniture and household items'/>
                        }
                      </span>
                      <p className='text-lg/7 mt-0.5 text-pretty'>Furniture and household items</p>
                    </div>
                  </div>
                  <Input
                    label='Value'
                    placeholder='0'
                    inputId='furnitureValue'
                    defaultValue={state.business.financialPosition.furnitureValue || ''}
                    {...register('furnitureValue', { validate: (value) => validateMoney(false, value) })}
                    error={errors.furnitureValue}
                    money={true}
                  />
                  <Input
                    label='Amount owed'
                    placeholder='0'
                    inputId='furnitureAmount'
                    defaultValue={state.business.financialPosition.furnitureAmount || ''}
                    {...register('furnitureAmount', { validate: (value) => validateMoney(false, value) })}
                    error={errors.furnitureAmount}
                    money={true}
                  />
                  <Input
                    label='Monthly payments'
                    placeholder='0'
                    inputId='furnitureMonthlyPayment'
                    defaultValue={state.business.financialPosition.furnitureMonthlyPayment || ''}
                    {...register('furnitureMonthlyPayment', { validate: (value) => validateMoney(false, value) })}
                    error={errors.furnitureMonthlyPayment}
                    money={true}
                  />
                </div>
                <div className='grid grid-cols-1 lg:grid-cols-4 gap-5 lg:gap-8 _hide-label-lg border-b border-[var(--form-box-divider)] pt-2 px-3 lg:px-0 pb-8 lg:pb-4 mb-2 last:mb-0 lg:mb-0'>
                  <div className='pt-2'>
                    <div className='flex w-full items-center gap-5'>
                      <span className='size-8 shrink-0 flex items-center justify-center text-[var(--theme-icon)]'>
                        {!isDealer ?
                          <ReactSVG className='size-full flex items-center justify-center' src='/icons/financial-position/udc/loans.svg' aria-label='Loans'/>
                          :
                          <ReactSVG className='FinancialPosition-svg size-full flex items-center justify-center' src={getSVGIcon('loans', '/icons/financial-position/loans.svg')} aria-label='Loans'/>
                        }
                      </span>
                      <p className='text-lg/7 mt-0.5'>Loans <TooltipIcon id='tooltip-holder' content='Enter any other loans you have and the amount you pay on these each month. These may include personal loans, student loans, hire purchase or buy now pay later arrangements.' /></p>
                    </div>
                  </div>
                  <Input
                    label='Value'
                    placeholder='0'
                    inputId='personalLoanValue'
                    defaultValue={state.business.financialPosition.personalLoanValue || ''}
                    {...register('personalLoanValue', { validate: (value) => validateMoney(false, value) })}
                    error={errors.personalLoanValue}
                    money={true}
                  />
                  <Input
                    label='Amount owed'
                    placeholder='0'
                    inputId='personalLoanAmount'
                    defaultValue={state.business.financialPosition.personalLoanAmount || ''}
                    {...register('personalLoanAmount', { validate: (value) => validateMoney(false, value) })}
                    error={errors.personalLoanAmount}
                    money={true}
                  />
                  <Input
                    label='Monthly payments'
                    placeholder='0'
                    inputId='personalLoanMonthlyPayment'
                    defaultValue={state.business.financialPosition.personalLoanMonthlyPayment || ''}
                    {...register('personalLoanMonthlyPayment', { validate: (value) => validateMoney(false, value) })}
                    error={errors.personalLoanMonthlyPayment}
                    money={true}
                  />
                </div>
                <div className='grid grid-cols-1 lg:grid-cols-4 gap-5 lg:gap-8 _hide-label-lg border-b border-[var(--form-box-divider)] pt-2 px-3 lg:px-0 pb-8 lg:pb-4 mb-2 last:mb-0 lg:mb-0'>
                  <div className='pt-2'>
                    <div className='flex w-full items-center gap-5'>
                      <span className='size-8 shrink-0 flex items-center justify-center text-[var(--theme-icon)]'>
                        {!isDealer ?
                          <ReactSVG className='size-full flex items-center justify-center' src='/icons/financial-position/udc/credit-card.svg' aria-label='Credit Card'/>
                          :
                          <ReactSVG className='FinancialPosition-svg size-full flex items-center justify-center' src={getSVGIcon('creditCards', '/icons/financial-position/credit-card.svg')} aria-label='Credit Card'/>
                        }
                      </span>
                      <p className='text-lg/7 mt-0.5'>Credit cards <TooltipIcon id='tooltip-holder' content='Enter your credit card limit and the average amount you repay each month.' /></p>
                    </div>
                  </div>
                  <Input
                    label='Value'
                    placeholder='0'
                    inputId='creditCardValue'
                    defaultValue={state.business.financialPosition.creditCardValue || ''}
                    {...register('creditCardValue', { validate: (value) => validateMoney(false, value) })}
                    error={errors.creditCardValue}
                    money={true}
                  />
                  <Input
                    label='Amount owed'
                    placeholder='0'
                    inputId='creditCardAmount'
                    defaultValue={state.business.financialPosition.creditCardAmount || ''}
                    {...register('creditCardAmount', { validate: (value) => validateMoney(false, value) })}
                    error={errors.creditCardAmount}
                    money={true}
                  />
                  <Input
                    label='Monthly payments'
                    placeholder='0'
                    inputId='creditCardMonthlyPayment'
                    defaultValue={state.business.financialPosition.creditCardMonthlyPayment || ''}
                    {...register('creditCardMonthlyPayment', { validate: (value) => validateMoney(false, value) })}
                    error={errors.creditCardMonthlyPayment}
                    money={true}
                  />
                </div>
                <div className='grid grid-cols-1 lg:grid-cols-4 gap-5 lg:gap-8 _hide-label-lg border-b border-[var(--form-box-divider)] pt-2 px-3 lg:px-0 pb-8 lg:pb-4 mb-2 last:mb-0 lg:mb-0'>
                  <div className='pt-2'>
                    <div className='flex w-full items-center gap-5'>
                      <span className='size-8 shrink-0 flex items-center justify-center text-[var(--theme-icon)]'>
                        {!isDealer ?
                          <ReactSVG className='size-full flex items-center justify-center' src='/icons/financial-position/udc/others.svg' aria-label='Others'/>
                          :
                          <ReactSVG className='FinancialPosition-svg size-full flex items-center justify-center' src={getSVGIcon('others', '/icons/financial-position/others.svg')} aria-label='Others'/>
                        }
                      </span>
                      <p className='text-lg/7 mt-0.5'>Others</p>
                    </div>
                  </div>
                  <Input
                    label='Value'
                    placeholder='0'
                    inputId='otherValue'
                    defaultValue={state.business.financialPosition.otherValue || ''}
                    {...register('otherValue', { validate: (value) => validateMoney(false, value) })}
                    error={errors.otherValue}
                    money={true}
                  />
                  <Input
                    label='Amount owed'
                    placeholder='0'
                    inputId='otherAmount'
                    defaultValue={state.business.financialPosition.otherAmount || ''}
                    {...register('otherAmount', { validate: (value) => validateMoney(false, value) })}
                    error={errors.otherAmount}
                    money={true}
                  />
                  <Input
                    label='Monthly payments'
                    placeholder='0'
                    inputId='otherMonthlyPayment'
                    defaultValue={state.business.financialPosition.otherMonthlyPayment || ''}
                    {...register('otherMonthlyPayment', { validate: (value) => validateMoney(false, value) })}
                    error={errors.otherMonthlyPayment}
                    money={true}
                  />
                </div>
                <div className='flex bg-[var(--theme-bg)] py-8 px-6 items-baseline justify-between mt-8 -mb-4'>
                  <p className='text-lg/7 grow'>Total monthly payments</p>
                  <p className='font-bold text-lg/7'>$&nbsp;{totalMonthlyPayments.toFixed(2)}</p>
                </div>
              </div>
            </Box>
            <Box>
              <div>
                <Textarea
                  label='Additional notes'
                  placeholder='Tell us more'
                  id='additionalNotes'
                  defaultValue={state.business.financialPosition.additionalNotes}
                  {...register('additionalNotes')}
                  error={errors.additionalNotes}
                />
              </div>
            </Box>

            {state.isPostCall && (
              <p className="text-center text-gray-600 mt-6 max-w-2xl mx-auto">By clicking "Next" you are confirming that the above information is correct</p>
            )}

            <div className='flex justify-between mt-8 pb-16 max-w-5xl mx-auto'>
              <Button iconPosition='left' onClick={() => navigate(ROUTES.BUSINESS.ABOUT_YOU.path)}>Back</Button>
              <Button type='submit'>Next</Button>
            </div>
          </>
        }
      </form>

    </>
  )
}

export default FinancialPosition
